import {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useState,
  forwardRef,
  useImperativeHandle,
} from 'react';
import { Box, IconButton, Stack, Grid } from '@mui/material';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import CustomText from 'components/common/CustomText/CustomText';
import { CPT } from 'types/types/Types';
import { useGetCPTSuggestions } from 'hooks/useLookups';
import { colors } from 'styles/colors';
import { hexWithOpacity } from 'common/utils';
import { snackbarService } from 'services/snackbarService';

export interface SuggestedCPTsRef {
  fetchSuggestions: () => void;
}

interface Props {
  procedureName: string;
  selectedCPTs: CPT[];
  setSelectedCPTs: (cpts: CPT[]) => void;
  onLoadingChange: (loading: boolean) => void;
}

const SuggestedCPTs = forwardRef<SuggestedCPTsRef, Props>(
  ({ procedureName, selectedCPTs, setSelectedCPTs, onLoadingChange }, ref) => {
    const [show, setShow] = useState(true);
    const [suggestions, setSuggestions] = useState<CPT[]>([]);
    const [originalSuggestions, setOriginalSuggestions] = useState<CPT[]>([]);
    const [hasSearched, setHasSearched] = useState(false);

    const suggestionsLength = useMemo(
      () => suggestions.length,
      [suggestions]
    );

    const { refetch, isFetching, isFetched } = useGetCPTSuggestions(procedureName, {
      enabled: false,
      onSuccess: (data: CPT[]) => {
        setOriginalSuggestions(data);
      },
    });

    const fetchSuggestions = useCallback(() => {
      if (!procedureName) {
        snackbarService.showSnackbar('Please enter a procedure name/type', 'info');
        return;
      }
      setHasSearched(true);
      refetch();
    }, [refetch, procedureName]);


    useImperativeHandle(ref, () => ({
      fetchSuggestions,
    }));

    useEffect(() => {
      onLoadingChange(isFetching);
    }, [isFetching, onLoadingChange]);

    useEffect(() => {
      setSuggestions(
        originalSuggestions.filter(
          cpt => !selectedCPTs.some(sel => sel.id === cpt.id)
        )
      );
    }, [selectedCPTs, originalSuggestions]);

    const handleSelect = (item: CPT) => {
      if (!selectedCPTs.some(cpt => cpt.id === item.id)) {
        setSelectedCPTs([...selectedCPTs, { ...item, quantity: 1 }]);
      }
    };

    const handleToggleSuggestions = useCallback(() => {
      setShow(prev => !prev);
    }, [show, procedureName, originalSuggestions.length, refetch]);



    return (
      <Box>
        {hasSearched && !isFetching && originalSuggestions.length === 0 && (
          <Box display="flex" justifyContent="center" my={2}>
            <CustomText
              text="No suggested CPTs found for this procedure"
              color={colors.secondary}
              textAlign="center"
            />
          </Box>
        )}

        {(show && suggestionsLength > 0) && (
          <Grid container spacing={1.5} sx={{ px: 2.5, my: 2 }}>
            {suggestions.map(item => (
              <Grid key={item.id} size={{ xs: 12, md: 6 }}>
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  p={1}
                  borderRadius="10px"
                  border={`1px solid ${colors.itemBorder}`}
                >
                  <Box>
                    <CustomText
                      text={item.code}
                      color={hexWithOpacity(colors.primary, 0.8)}
                    />
                    <CustomText
                      text={item.value}
                      color={colors.secondary}
                    />
                  </Box>

                  <IconButton
                    size="small"
                    onClick={() => handleSelect(item)}
                    sx={{ color: colors.success }}
                  >
                    <AddCircleIcon sx={{ fontSize: 20 }} />
                  </IconButton>
                </Box>
              </Grid>
            ))}
          </Grid>
        )}

        {/* Toggle button must ALWAYS render */}
        {(suggestionsLength > 0) && (
          <Box display="flex" justifyContent="center" mb={1}>
            <IconButton
              onClick={handleToggleSuggestions}
              sx={{ color: colors.primary }}
            >
              <CustomText
                text={show ? `Hide Suggestions(${suggestionsLength})` : `Show Suggestions(${suggestionsLength})`}
                color={colors.appeal}
                textAlign={'center'}
              />
            </IconButton>
          </Box>)}
      </Box>
    );
  }
);

export default memo(SuggestedCPTs);
